import { useTheme } from "@/contexts/ThemeContext";
import React, { useState } from "react";
import { Compass, Building2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useTour } from "@/contexts/TourContext";
import { listCompanies } from "@/lib/api";
import { TOUR_COMPANIES } from "@/lib/tourSteps";

export const TourLauncher = ({ variant = "light" }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { start } = useTour();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(null);

  if (!user || !["admin", "consultor"].includes(user.role)) return null;

  const openDialog = async () => {
    setOpen(true);
    setOptions(null);
    const d = await listCompanies().catch(() => ({ companies: [] }));
    setOptions(
      TOUR_COMPANIES.map((cfg) => {
        const c = (d.companies || []).find((x) => x.session_id === cfg.sessionId);
        const agent = c && (c.agents.find((a) => a.department === cfg.department) || c.agents[0]);
        return { ...cfg, agentSlug: agent?.slug, agentName: agent?.name };
      })
    );
  };

  const choose = (opt) => {
    setOpen(false);
    start(opt);
  };

  const dark = variant === "dark" && theme?.mode !== "light";
  return (
    <>
      <Button
        onClick={openDialog}
        variant="outline"
        className={`rounded-sm text-[11px] uppercase tracking-[0.2em] ${
          dark ? "border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white" : "border-slate-300 text-slate-700 hover:bg-slate-100"
        }`}
        data-testid="tour-launch-btn"
      >
        <Compass className="mr-2 h-3.5 w-3.5" /> Tour guiado
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="tour-company-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium">Tour guiado</DialogTitle>
            <DialogDescription>
              9 pasos: entrevista, informe, flujo de actividades, agentes, MCP, biblioteca KBL, chat, métricas y dashboard. Elija la empresa demo.
            </DialogDescription>
          </DialogHeader>
          {!options ? (
            <div className="flex items-center gap-2 py-6 text-sm text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando empresas…
            </div>
          ) : (
            <div className="grid gap-3">
              {options.map((o) => (
                <button
                  key={o.key}
                  disabled={!o.agentSlug}
                  onClick={() => choose(o)}
                  className="flex items-center gap-3 border border-slate-200 p-4 text-left transition-colors hover:border-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid={`tour-company-${o.key}`}
                >
                  <span className="flex h-9 w-9 items-center justify-center bg-slate-900 text-accent-blue-light">
                    <Building2 className="h-4 w-4" />
                  </span>
                  <span>
                    <span className="block font-display text-base text-slate-900">{o.company}</span>
                    <span className="block text-xs text-slate-500">
                      {o.agentSlug ? o.blurb : "Demo no cargada · restáurela en Admin → Demo"}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
